<template>
  <a href="#main" class="c2c-skip-link">{{ t('skip.to_main') }}</a>
  <div class="c2c-app">
    <CallbackScreen v-if="route === 'callback'" />

    <div v-else-if="restoringSession" class="c2c-restoring" aria-busy="true"></div>

    <template v-else-if="!isLoggedIn">
      <header class="c2c-pre-header">
        <LanguagePicker />
      </header>
      <main id="main">
        <LoginScreen />
      </main>
      <AppFooter />
    </template>

    <template v-else>
      <header class="c2c-header">
        <a href="/" class="c2c-brand-row">
          <div class="c2c-logo" aria-hidden="true">
            <svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" width="32" height="32">
              <rect width="64" height="64" rx="14" fill="#3366cc"/>
              <text x="32" y="40" font-family="-apple-system, sans-serif" font-size="20" font-weight="800" fill="white" text-anchor="middle" letter-spacing="-0.5">c2c</text>
            </svg>
          </div>
          <div class="c2c-brand">{{ t('app.title') }}</div>
        </a>
        <div class="c2c-header-right">
          <LanguagePicker />
          <div class="c2c-user" v-if="userName">
            <span>{{ userName }}</span>
            <button class="c2c-logout" @click="onLogout">
              <LogOut :size="15" aria-hidden="true" />
              <span>{{ t('app.logout') }}</span>
            </button>
          </div>
        </div>
      </header>

      <main id="main" class="c2c-main">
        <!-- Browser capability banner: shown when WebCodecs is missing or
             when the browser can only do passthrough. -->
        <CapabilityBanner
          :capabilities="capabilities"
          :watch-visibility="state === 'encoding' || state === 'uploading'"
        />

        <!-- Resumable uploads (Feature #2). Only shown on idle so it doesn't
             interrupt an in-flight upload. -->
        <ResumeBanner
          v-if="state === 'idle' && capabilities?.canPassthrough"
          ref="resumeBanner"
          @resume="onResumeRequested"
          @resume-stored="onResumeStored"
        />

        <!-- Recent uploads sidebar. Collapsed by default; expands on click
             and lazy-loads from the Commons API. Useful for "did my upload
             actually go through?" continuity checks across sessions. -->
        <UploadHistory
          v-if="state === 'idle' && userName"
          :username="userName"
        />

        <!-- Batch queue overview (Feature #1). Shown whenever a queue exists,
             including while items are processing. -->
        <BatchQueue
          v-if="batchItems.length > 0 && state !== 'idle'"
          :items="batchItems"
          :current-index="batchProcessing ? batchProcessingIndex : batchCurrentIndex"
          @remove="onBatchRemove"
          @select="onBatchSelect"
        />

        <!--
          Adding to the queue after the fact. Sits directly under the queue
          it feeds, and starts collapsed: a second dotted dropzone open by
          default pushed the queue off the screen and competed with the one
          control that matters while files are waiting.
        -->
        <div v-if="canAddMore" class="c2c-addmore">
          <button
            type="button"
            class="c2c-addmore-toggle"
            :aria-expanded="addMoreOpen"
            @click="addMoreOpen = !addMoreOpen"
          >
            <component :is="addMoreOpen ? Minus : Plus" :size="15" aria-hidden="true" />
            <span>{{ t('queue.add_more') }}</span>
            <small>{{ t('queue.slots_left', { n: queueSlotsLeft, limit: MAX_QUEUE_FILES }) }}</small>
          </button>

          <FilePicker
            v-if="addMoreOpen"
            :loading="false"
            multiple
            compact
            :max-files="queueSlotsLeft"
            :passthrough-only="capabilities ? !capabilities.canEncode : false"
            :max-file-size="capabilities?.effectiveMaxBytes ?? MAX_FILE_SIZE_BYTES"
            @file="onAddMoreFiles([$event])"
            @files="onAddMoreFiles"
          />
        </div>
        <p v-else-if="state === 'ready' && queueSlotsLeft === 0" class="c2c-addmore-full">
          {{ t('queue.limit_reached', { limit: MAX_QUEUE_FILES }) }}
        </p>

        <FilePicker
          v-if="(state === 'idle' || state === 'analyzing') && capabilities?.canPassthrough"
          :loading="state === 'analyzing'"
          :multiple="true"
          :passthrough-only="capabilities && !capabilities.canEncode"
          :max-file-size="capabilities?.effectiveMaxBytes"
          @file="onFileSelected"
          @files="onFilesSelected"
        />

        <!-- SHA-1 duplicate-check progress, shown when a hash is in flight
             but the form is already visible. Non-blocking advisory; the
             Skip button lets the user dismiss it for impatient cases. -->
        <p
          v-if="state === 'ready' && duplicateCheckProgress !== null && duplicateCheckProgress < 100"
          class="c2c-dupe-progress"
        >
          {{ t('duplicate.checking', { pct: duplicateCheckProgress }) }}
          <button type="button" class="c2c-dupe-skip" @click="skipDuplicateCheck">
            {{ t('duplicate.skip') }}
          </button>
        </p>

        <section v-else-if="state === 'ready' && file && meta" class="c2c-form-section">
          <FileMetadata :file="file" :meta="meta" :passthrough="willPassthrough" />

          <!-- Trim control (Feature #4). Collapsed by default; auto-opens
               when the source is over the duration cap. -->
          <TrimControl
            :duration="meta.duration"
            :file="file"
            v-model="trimModel"
            :mediaMode="form.mediaMode ?? 'video-audio'"
            :hasAudio="!!meta.audioCodec"
            @update:mediaMode="form.mediaMode = $event"
            @duration-update="onTrimDurationUpdate"
          />

          <label v-if="isBatchEditing && batchItems.length > 1 && isSourceAvailable" class="c2c-apply-all">
            <input type="checkbox" v-model="applyMetadataToAll" @change="onLiveMetadataToggle" />
            <span>{{ t('queue.keep_information_same') }}</span>
          </label>

          <form class="c2c-form" @submit.prevent="onSubmit" @keydown.enter="onFormEnter">
            <div class="c2c-field">
              <div class="c2c-field-header">
                <label for="title">{{ t('form.title_label') }} <span class="c2c-required">{{ t('form.title_required') }}</span></label>
                <CdxButton type="button" size="small" :disabled="!form.title" @click="copyTitle">
                  {{ titleCopyOk ? t('states.copied') : t('form.copy') }}
                </CdxButton>
              </div>
              <input
                id="title"
                v-model="form.title"
                type="text"
                :placeholder="t('form.title_placeholder')"
                required
                :maxlength="usableTitleLength"
                :aria-invalid="!!titleError"
                :aria-describedby="titleError ? 'title-error' : 'title-help'"
                autocomplete="off"
              />
              <small v-if="!titleError" id="title-help">
                {{ form.title.trim()
                  ? t('form.title_help', { filename: sanitizeTitle(form.title) + '.' + outputExtension, len: sanitizedTitleLength, max: usableTitleLength })
                  : t('form.title_help_empty') }}
              </small>
              <small v-else id="title-error" class="c2c-field-error">{{ titleError }}</small>
              <small v-if="titleAvailability === 'taken'" class="c2c-field-warn">
                {{ t('form.title_taken', { candidate: sanitizeTitle(form.title) + ' (2).' + outputExtension }) }}
              </small>
              <small v-else-if="titleAvailability === 'checking'" class="c2c-field-info">
                {{ t('form.title_checking') }}
              </small>
              <small v-else-if="titleAvailability === 'available'" class="c2c-field-ok">
                {{ t('form.title_available') }}
              </small>
            </div>

            <div class="c2c-field">
              <div class="c2c-field-header">
                <span class="c2c-field-grouplabel">
                  {{ t('form.caption_label') }}
                  <!-- Commons does not require a caption, but its structured
                       data modelling guidance lists one in at least one
                       language as a basic item for every file, so the label
                       says "recommended" rather than "optional". -->
                  <span class="c2c-recommended">{{ t('form.recommended') }}</span>
                </span>
              </div>
              <MultilingualTextEditor
                v-model="form.captions"
                captions-only
                :hint="t('form.caption_hint')"
                :placeholder="t('form.caption_placeholder', { max: MAX_CAPTION_LENGTH })"
                :add-label="t('form.caption_add_language')"
                :max-length="MAX_CAPTION_LENGTH"
                :soft-limit="MAX_CAPTION_LENGTH"
                :rows="2"
              />
            </div>

            <div class="c2c-field">
              <div class="c2c-field-header">
                <span class="c2c-field-grouplabel">
                  {{ t('form.description_label') }}
                  <span class="c2c-optional">{{ t('form.optional') }}</span>
                </span>
                <CdxButton type="button" size="small" :disabled="!primaryDescription" @click="copyDescription">
                  {{ descriptionCopyOk ? t('states.copied') : t('form.copy') }}
                </CdxButton>
              </div>
              <MultilingualTextEditor
                v-model="form.descriptions"
                :hint="t('form.description_hint')"
                :placeholder="t('form.description_placeholder', { max: SUGGESTED_DESCRIPTION_LENGTH })"
                :add-label="t('form.description_add_language')"
                :max-length="2000"
                :soft-limit="SUGGESTED_DESCRIPTION_LENGTH"
                :rows="3"
              />
            </div>

            <div class="c2c-row">
              <div class="c2c-field">
                <label for="date">
                  {{ t('form.date_label') }}
                  <span class="c2c-label-hint">{{ t('form.date_hint') }}</span>
                </label>
                <DateField
                  v-model="form.date"
                  :max="todayStr"
                  input-id="date"
                  @update:valid="dateValid = $event"
                />
              </div>

              <div v-if="!willPassthrough && !isAudioOnly" class="c2c-field">
                <label for="resolution">{{ t('form.resolution_label') }}</label>
                <SelectField
                  v-model="form.resolution"
                  :options="resolutionOptions"
                  :label="t('form.resolution_label')"
                  input-id="resolution"
                />
              </div>
            </div>

            <SourceProvenance v-model="form.provenance" />

            <template v-if="form.provenance.kind === 'own'">
              <label class="c2c-toggle">
                <input type="checkbox" v-model="showAuthorOverride" />
                <span>{{ t('form.show_author_override') }}</span>
              </label>
              <div v-if="showAuthorOverride" class="c2c-row">
                <div class="c2c-field">
                  <label for="authorOverride">
                    {{ t("form.author_override") }}
                    <small>{{ t("form.author_override_help") }}</small>
                  </label>
                  <small class="c2c-field-note">{{ t("form.author_override_examples") }}</small>
                  <input
                    id="authorOverride"
                    v-model="form.authorOverride"
                    type="text"
                    maxlength="200"
                    :placeholder="t('form.author_override_placeholder')"
                  />
                  <button
                    v-if="form.authorOverride"
                    type="button"
                    class="c2c-inline-btn"
                    @click="resetAuthorOverride"
                  >
                    {{ t("form.author_override_reset") }}
                  </button>
                </div>
              </div>
            </template>

            <label class="c2c-toggle">
              <input type="checkbox" v-model="showWikidata" />
              <span>{{ t('form.show_wikidata') }}</span>
            </label>
            <WikidataItemPicker v-if="showWikidata" v-model="form.wikidataItem" />

            <CategoryAutocomplete v-model="form.categories" />

            <!-- Plain-language preamble explaining what's about to happen.
                 Reduces "is it broken?" complaints during the long encode.
                 Hidden when batch-editing because the queue UI already
                 sets the right expectations. -->
            <aside v-if="!isBatchEditing" class="c2c-preamble" role="note" aria-live="polite">
              <h3>{{ t('preamble.heading') }}</h3>
              <ul>
                <li>{{ willPassthrough
                  ? t('preamble.passthrough_time')
                  : t('preamble.encode_time') }}</li>
                <li>{{ t('preamble.tab_open') }}</li>
                <li>{{ t('preamble.resume') }}</li>
                <li v-if="!willPassthrough">{{ t('preamble.may_freeze') }}</li>
              </ul>
            </aside>

            <!-- Inline disabled-button reason. Renders EVERY blocking
                 condition so users see the full fix-list at once instead
                 of fixing one issue and being immediately told about the
                 next. Looks like a checklist. -->
            <div
              v-if="!canSubmit && submitDisabledReasons.length > 0"
              class="c2c-submit-reason"
              role="alert"
            >
              <AlertTriangle :size="16" />
              <div class="c2c-submit-reason-body">
                <strong>{{ t('submit_disabled.heading', { n: submitDisabledReasons.length }) }}</strong>
                <ul>
                  <li v-for="(reason, i) in submitDisabledReasons" :key="i">{{ reason }}</li>
                </ul>
              </div>
            </div>

            <div class="c2c-actions">
              <CdxButton type="button" @click="resetAll">{{ t('form.cancel') }}</CdxButton>
              <CdxButton
                type="submit"
                action="progressive"
                weight="primary"
                :disabled="!canSubmit || batchProcessing"
              >
                {{ submitButtonLabel }}
              </CdxButton>
            </div>
          </form>
        </section>

        <section v-else-if="state === 'encoding'" class="c2c-progress-section">
          <h2>{{ encoderPhase === "passthrough" ? t('encoding.preparing') : t('encoding.converting') }}</h2>
          <div class="c2c-bar" role="progressbar" :aria-valuenow="encodeProgressPct" aria-valuemin="0" aria-valuemax="100" :aria-label="t('encoding.converting')">
            <div class="c2c-bar-fill" :style="{ width: encodeProgressPct + '%' }"></div>
          </div>
          <div class="c2c-progress-meta">
            <span>{{ encodeProgressPct }}%</span>
            <span v-if="encodeETA && encoderPhase !== 'passthrough'">{{ t('encoding.remaining', { time: encodeETA }) }}</span>
          </div>
          <p class="c2c-hint">
            {{ encoderPhase === "passthrough" ? t('encoding.passthrough_hint') : t('encoding.encoding_hint') }}
          </p>
          <div class="c2c-actions" style="justify-content: center">
            <CdxButton action="destructive" @click="askCancelEncode">{{ t('form.cancel') }}</CdxButton>
          </div>
        </section>

        <section v-else-if="state === 'uploading'" class="c2c-progress-section">
          <h2>{{ resumingExisting ? t('uploading.resuming_title') : t('uploading.title') }}</h2>
          <div class="c2c-bar" role="progressbar" :aria-valuenow="uploadProgressPct" aria-valuemin="0" aria-valuemax="100" :aria-label="t('uploading.title')">
            <div class="c2c-bar-fill c2c-bar-fill--upload" :style="{ width: uploadProgressPct + '%' }"></div>
          </div>
          <div class="c2c-progress-meta">
            <span>{{ uploadProgressPct }}%</span>
            <span>{{ formatBytes(uploadedBytes) }} / {{ formatBytes(encodedBytes) }}</span>
            <span v-if="uploadSpeed">{{ uploadSpeed }}</span>
            <span v-if="uploadETA">{{ t('encoding.remaining', { time: uploadETA }) }}</span>
          </div>
          <p class="c2c-hint">{{ t('uploading.hint', { size: formatBytes(encodedBytes) }) }}</p>
          <div class="c2c-actions" style="justify-content: center">
            <CdxButton action="destructive" @click="askCancelUpload">{{ t('form.cancel') }}</CdxButton>
          </div>
        </section>

        <template v-else-if="state === 'done' && result">
          <ResultCard :result="result" @new="resetAll" />
          <!-- The file is published either way; captions are a separate
               Wikibase edit that can fail on its own. Say so plainly rather
               than letting the contributor assume they were saved. -->
          <CdxMessage
            v-if="uploader.captionWarning.value"
            type="warning"
            class="c2c-caption-warning"
          >
            {{ t('form.caption_failed', { reason: uploader.captionWarning.value }) }}
          </CdxMessage>
          <!-- Same story for the optional Wikidata statement: a separate
               edit on a separate wiki, so it reports its own outcome. -->
          <CdxMessage
            v-if="uploader.wikidataWarning.value"
            type="warning"
            class="c2c-caption-warning"
          >
            {{ t('form.wikidata_failed', { reason: uploader.wikidataWarning.value }) }}
          </CdxMessage>
          <CdxMessage
            v-else-if="uploader.wikidataLink.value"
            :type="uploader.wikidataLink.value.alreadyPresent ? 'notice' : 'success'"
            class="c2c-caption-warning"
          >
            <template v-if="uploader.wikidataLink.value.alreadyPresent">
              {{ t('form.wikidata_already_present', { label: wikidataLinkLabel }) }}
            </template>
            <template v-else>
              {{ t('form.wikidata_added', {
                label: wikidataLinkLabel,
                property: wikidataLinkPropertyLabel
              }) }}
            </template>
            <a :href="wikidataLinkUrl" target="_blank" rel="noopener noreferrer">
              {{ uploader.wikidataLink.value.qid }}
            </a>
          </CdxMessage>
          <!-- Live transcode status (Feature #6) - polls every 30s until all
               renditions complete or fail. -->
          <TranscodeStatusCard :filename="result.filename" />
        </template>

        <section v-else-if="state === 'cancelled'" class="c2c-info-section">
          <h2>{{ t('states.cancelled_heading') }}</h2>
          <p>{{ t('states.cancelled_desc') }}</p>
          <div class="c2c-actions" style="justify-content: center">
            <CdxButton action="progressive" weight="primary" @click="resetAll">
              {{ t('states.start_over') }}
            </CdxButton>
          </div>
        </section>

        <section v-else-if="state === 'error'" class="c2c-error-section">
          <h2>{{ t('states.error_heading') }}</h2>
          <pre class="c2c-error-msg">{{ errorMessage }}</pre>

          <!-- Resume offer when the failure happened mid-upload AND we still
               have the bytes in memory (Feature #2, in-session path). -->
          <p v-if="canResumeInSession" class="c2c-resume-inline">
            {{ t('states.resume_offer', { percent: inSessionResumePercent }) }}
          </p>

          <div class="c2c-actions">
            <CdxButton @click="copyError">{{ copyOk ? t('states.copied') : t('states.copy_error') }}</CdxButton>
            <CdxButton
              v-if="canResumeInSession"
              action="progressive"
              weight="primary"
              @click="onResumeInSession"
            >
              {{ t('states.resume_button') }}
            </CdxButton>
            <CdxButton action="progressive" :weight="canResumeInSession ? 'normal' : 'primary'" @click="resetAll">
              {{ t('states.try_another') }}
            </CdxButton>
          </div>
        </section>
      </main>

      <!-- Minimal feedback strip on the upload-form view. The full footer
           (maintainer / source / license / V2C blurb) only renders on the
           login screen above; once a user is in the app, the same
           information is one click away on the home page, so the in-app
           footer is reduced to a single bug-report link that opens the
           maintainer's talk page with a pre-filled section header. -->
      <p class="c2c-feedback-strip">
        <a :href="feedbackUrl" target="_blank" rel="noopener noreferrer">
          <Megaphone :size="14" />
          {{ t('feedback.report_bug') }}
        </a>
      </p>

      <!-- Surfaces server-side warnings (duplicate, badfilename, etc.) when
           the finalize step returns result=Warning. The user explicitly
           accepts before we re-finalize with ignorewarnings=1. -->
      <UploadWarningsDialog
        v-if="uploader.pendingWarnings.value"
        :warnings="uploader.pendingWarnings.value.warnings"
        @cancel="onWarningsCancel"
        @confirm="onWarningsConfirm"
      />
    </template>

    <!-- Global one-shot dialog (replaces blocking alert/confirm). -->
    <AppDialog
      :visible="!!globalDialog"
      :title="globalDialog?.title || ''"
      :message="globalDialog?.message || ''"
      :tone="globalDialog?.tone || 'info'"
      :confirm-label="globalDialog?.confirmLabel || ''"
      :cancel-label="globalDialog?.cancelLabel"
      @confirm="confirmGlobalDialog"
      @cancel="dismissGlobalDialog"
    />

    <!-- Pre-upload duplicate-detection dialog. Surfaces when SHA-1 of the
         picked file matches a file already on Commons. The user can still
         choose to upload anyway (e.g., re-uploading after deletion or
         under a different license), but they're warned first so they
         don't waste minutes encoding. -->
    <AppDialog
      :visible="duplicateDialogOpen"
      :title="t('duplicate.dialog_title')"
      :message="duplicateDialogMessage"
      tone="warn"
      :confirm-label="t('duplicate.continue_anyway')"
      :cancel-label="t('duplicate.cancel_upload')"
      @confirm="duplicateDialogOpen = false"
      @cancel="onDuplicateCancel"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue"
import { AlertTriangle, LogOut, Megaphone, Minus, Plus } from "lucide-vue-next"
import { useI18n } from "vue-i18n"
import { CdxButton, CdxMessage } from "@wikimedia/codex"
import LoginScreen from "@/components/LoginScreen.vue"
import CallbackScreen from "@/components/CallbackScreen.vue"
import LanguagePicker from "@/components/LanguagePicker.vue"
import FilePicker from "@/components/FilePicker.vue"
import FileMetadata from "@/components/FileMetadata.vue"
import ResultCard from "@/components/ResultCard.vue"
import CategoryAutocomplete from "@/components/CategoryAutocomplete.vue"
import WikidataItemPicker from "@/components/WikidataItemPicker.vue"
import DateField from "@/components/DateField.vue"
import SelectField from "@/components/SelectField.vue"
import { wikidataEntityUrl } from "@/lib/wikidata-api"
import MultilingualTextEditor from "@/components/MultilingualTextEditor.vue"
import AppFooter from "@/components/AppFooter.vue"
import TrimControl from "@/components/TrimControl.vue"
import TranscodeStatusCard from "@/components/TranscodeStatusCard.vue"
import ResumeBanner from "@/components/ResumeBanner.vue"
import UploadHistory from "@/components/UploadHistory.vue"
import BatchQueue from "@/components/BatchQueue.vue"
import CapabilityBanner from "@/components/CapabilityBanner.vue"
import SourceProvenance from "@/components/SourceProvenance.vue"
import UploadWarningsDialog from "@/components/UploadWarningsDialog.vue"
import AppDialog from "@/components/AppDialog.vue"
import { detectCapabilities, type Capabilities } from "@/lib/capability"
import { useEncoder } from "@/composables/useEncoder"
import { useUploader } from "@/composables/useUploader"
import { probeFile } from "@/lib/mp4-demux"
import {
  checkUser, defaultTitleFromFilename, sanitizeTitle,
  validateTitle, checkFilenameAvailable, UploadWarningsError,
  stashFileExists, findBySha1, payloadSize,
  type DuplicateMatch, type UploadPayload
} from "@/lib/commons-api"
import type {
  ResultMessage as Sha1ResultMessage,
  RequestMessage as Sha1RequestMessage
} from "@/workers/sha1.worker"
import { computeIdentityHash } from "@/lib/file-identity"
import { requestWakeLock, releaseWakeLock } from "@/lib/wake-lock"
import { clearResumeState, restoredPostUploadFields } from "@/lib/resume-storage"
import { saveDraft, loadDraftForFile, clearDraft } from "@/lib/form-draft"
import { classifyError } from "@/lib/errors"
import { openEncodedFile } from "@/lib/encoded-store"
import { getSavedLicense, saveLicense } from "@/lib/license-preference"
import {
  getAccessToken, isUserOAuthMode, logout as oauthLogout,
  getCachedUsername, setCachedUsername, restoreSession, hasPersistedSession
} from "@/lib/oauth"
import {
  MAX_DURATION_SECONDS, MAX_TITLE_LENGTH, SUGGESTED_DESCRIPTION_LENGTH,
  MAX_CAPTION_LENGTH, TOOL_FEEDBACK_URL, outputExtensionFor,
  WIKIDATA_PROP_AUDIO, MAX_QUEUE_FILES, MAX_FILE_SIZE_BYTES
} from "@/lib/config"
import type {
  VideoMeta, UploadForm, UploadResult, BatchItem, ResumeState, TrimRange
} from "@/lib/types"

const { t, locale } = useI18n()

const feedbackUrl = TOOL_FEEDBACK_URL

type AppState = "idle" | "analyzing" | "ready" | "encoding" | "uploading" | "done" | "error" | "cancelled"

const route = computed<"callback" | "main">(() => {
  if (typeof window === "undefined") return "main"
  const url = new URL(window.location.href)
  if (url.pathname.startsWith("/oauth/callback") || url.searchParams.has("code")) return "callback"
  return "main"
})

/**
 * getAccessToken reads sessionStorage, which Vue cannot track, so a silent
 * sign-in at boot would never re-render on its own. authVersion is bumped
 * whenever the session changes, to force the computed to re-read.
 */
const authVersion = ref(0)
const isLoggedIn = computed(() => {
  void authVersion.value
  return !!getAccessToken()
})
/** True while a remembered session is being resumed, so the login screen
 *  does not flash for a moment before the silent refresh lands. */
const restoringSession = ref(
  isUserOAuthMode() && !getAccessToken() && hasPersistedSession())

const state = ref<AppState>("idle")
const file = ref<File | null>(null)
const meta = ref<VideoMeta | null>(null)
const result = ref<UploadResult | null>(null)
const errorMessage = ref("")
const userName = ref<string>(getCachedUsername() || "")
const copyOk = ref(false)
const titleCopyOk = ref(false)
const descriptionCopyOk = ref(false)



/** Browser feature detection. `null` while detection is still running on
 *  app boot; an object with .canEncode / .canPassthrough flags afterwards.
 *  Used to gate the file picker and surface guidance for unsupported
 *  browsers (Firefox Android, older Safari, etc.). */
const capabilities = ref<Capabilities | null>(null)

/** Global one-shot dialog used for non-blocking notifications and confirmations
 *  that don't fit in their owning component (e.g., the resume re-encode notice
 *  or the legacy-mode logout warning). Replaces blocking alert()/confirm(). */
interface AppDialogState {
  title: string
  message: string
  tone: "info" | "warn" | "error"
  confirmLabel: string
  cancelLabel?: string
  onConfirm?: () => void
  onCancel?: () => void
}
const globalDialog = ref<AppDialogState | null>(null)

function showInfo(title: string, message: string, tone: "info" | "warn" | "error" = "info") {
  globalDialog.value = {
    title, message, tone,
    confirmLabel: t('common.ok')
  }
}

/** Ask before doing something destructive, reusing the global dialog. */
function showConfirm(
  title: string, message: string, confirmLabel: string,
  cancelLabel: string, onConfirm: () => void
) {
  globalDialog.value = { title, message, tone: "warn", confirmLabel, cancelLabel, onConfirm }
}

/**
 * Cancelling throws away the encode (or the partial upload) AND the typed
 * metadata, because both handlers call resetForNewWork(). One stray click
 * on a destructive button should not cost all that silently.
 */
function askCancelEncode() {
  showConfirm(
    t('states.cancel_encode_title'), t('states.cancel_encode_message'),
    t('states.cancel_confirm_yes'), t('states.cancel_confirm_no'), cancelEncode)
}
function askCancelUpload() {
  showConfirm(
    t('states.cancel_upload_title'), t('states.cancel_upload_message'),
    t('states.cancel_confirm_yes'), t('states.cancel_confirm_no'), cancelUpload)
}

/**
 * Enter in a single-line field used to submit the form, which starts the
 * whole convert-and-upload run. Only the submit button should do that.
 * Textareas keep their newline, and buttons keep their activation.
 */
function onFormEnter(e: KeyboardEvent) {
  const el = e.target as HTMLElement | null
  if (!el) return
  const tag = el.tagName
  if (tag === "TEXTAREA" || tag === "BUTTON") return
  e.preventDefault()
}

function dismissGlobalDialog() {
  const d = globalDialog.value
  globalDialog.value = null
  d?.onCancel?.()
}

function confirmGlobalDialog() {
  const d = globalDialog.value
  globalDialog.value = null
  d?.onConfirm?.()
}

/** Translate an unknown error into a user-facing string. Falls back to the
 *  raw message when no specific pattern matches. Used everywhere that a
 *  caught Error is about to be assigned to errorMessage. */
function friendlyError(err: unknown): string {
  const fe = classifyError(err)
  // The translator returns the raw key when there's no translation -
  // fall back to the raw message in that case so the user sees something.
  const translated = t(fe.key, fe.params || {})
  return translated && translated !== fe.key ? translated : fe.raw
}

/* The link result carries only the qid, so the friendly label comes from
   the form, which still holds what the user picked. Falls back to the qid
   if the form has since been reset. */
const wikidataLinkLabel = computed(() => {
  const link = uploader.wikidataLink.value
  if (!link) return ""
  const picked = form.value.wikidataItem
  return picked && picked.qid === link.qid && picked.label ? picked.label : link.qid
})
const wikidataLinkPropertyLabel = computed(() => {
  const link = uploader.wikidataLink.value
  if (!link) return ""
  return link.property === WIKIDATA_PROP_AUDIO
    ? t("form.wikidata_prop_audio")
    : t("form.wikidata_prop_video")
})
const wikidataLinkUrl = computed(() => {
  const link = uploader.wikidataLink.value
  return link ? wikidataEntityUrl(link.qid) : ""
})

const todayStr = new Date().toISOString().substring(0, 10)

/** The resolution list, rebuilt when the source dimensions arrive so the
 *  "Original" row can name them. */
const resolutionOptions = computed(() => [
  { value: "1080p", label: t("form.resolution_1080") },
  { value: "720p", label: t("form.resolution_720") },
  { value: "480p", label: t("form.resolution_480") },
  { value: "original", label: t("form.resolution_original", { w: meta.value?.width ?? 0, h: meta.value?.height ?? 0 }) }
])

const AUTHOR_OVERRIDE_KEY = "c2c_author_override"
function makeFreshForm(): UploadForm {
  return {
    title: "",
    captions: [{ lang: String(locale.value || "en"), text: "" }],
    descriptions: [{ lang: String(locale.value || "en"), text: "" }],
    date: todayStr,
    authorOverride: "",
    provenance: {
      kind: "own",
      ownership: { kind: "all-mine" },
      license: { kind: "preset", id: getSavedLicense("cc-by-sa-4.0") }
    },
    categories: [],
    resolution: "1080p",
    trim: null,
    mediaMode: "video-audio",
    wikidataItem: null
  }
}

const form = ref<UploadForm>(makeFreshForm())

/** Optional sections start closed, but open themselves when the form
 *  already carries a value (resuming, or switching batch items). */
const showAuthorOverride = ref(false)
const showWikidata = ref(false)

watch(() => [form.value.authorOverride, form.value.wikidataItem] as const,
  ([author, item]) => {
    if (author) showAuthorOverride.value = true
    if (item) showWikidata.value = true
  }, { immediate: true })
try {
  const stored = localStorage.getItem(AUTHOR_OVERRIDE_KEY)
  if (stored) {
    form.value.authorOverride = stored
  }
} catch {
  // Ignore localStorage failures (private browsing, etc.)
}

// Remember the license whenever the user picks a preset, so the next
// upload (single or batch) starts pre-selected with it. See T434084.
watch(
  () => {
    const p = form.value.provenance
    return (p.kind === "own" || p.kind === "third-party") ? p.license : undefined
  },
  (license) => {
    if (license?.kind === "preset") saveLicense(license.id)
  },
  { deep: true }
)

// Persist form fields across an unexpected reload (e.g. iOS WebKit
// reclaiming memory and reloading the tab), tied to the specific file
// so re-picking a different file does not silently inherit stale
// metadata. See T434086.
let formSaveTimer: ReturnType<typeof setTimeout> | undefined
watch(form, (val) => {
  if (!file.value) return
  clearTimeout(formSaveTimer)
  formSaveTimer = setTimeout(() => {
    saveDraft(file.value as File, val)
  }, 300)
}, { deep: true })

/** The description the Copy button acts on: the first row with text in it,
 *  falling back to the first row. Keeps the existing single-value button
 *  working now that the form holds one description per language. */
const primaryDescription = computed(() => {
  const rows = form.value.descriptions
  return (rows.find(d => d.text.trim()) ?? rows[0])?.text ?? ""
})

// TrimControl v-model wants TrimRange | null exactly. UploadForm has trim
// as optional (TrimRange | null | undefined), so we wrap with a computed.
const trimModel = computed<TrimRange | null>({
  get() { return form.value.trim ?? null },
  set(v) { form.value.trim = v }
})
const encoder = useEncoder()
const uploader = useUploader()

/* ------------------------------------------------------------------ */
/*  Batch queue state (Feature #1)                                    */
/* ------------------------------------------------------------------ */

const batchItems = ref<BatchItem[]>([])
const batchCurrentIndex = ref(-1)
const applyMetadataToAll = ref(false)
const batchProcessing = ref(false)
const batchProcessingIndex = ref(-1)
const batchWarningContext = ref<{ item: BatchItem; form: UploadForm } | null>(null)
const batchResumeContext = ref<{ item: BatchItem; meta: VideoMeta; form: UploadForm } | null>(null)
let isHydratingBatchForm = false
const SOURCE_BATCH_INDEX = 0

const isBatchEditing = computed(() => batchItems.value.length > 0)
const sourceBatchItem = computed(() => batchItems.value[SOURCE_BATCH_INDEX])
const isSourceAvailable = computed(() => sourceBatchItem.value?.status === "pending")
const isEditingBatchSource = computed(() =>
  batchCurrentIndex.value === SOURCE_BATCH_INDEX && isSourceAvailable.value
)

watch([
  () => form.value.title,
  () => form.value.captions,
  () => form.value.descriptions,
  () => form.value.date,
  () => form.value.provenance,
  () => form.value.categories,
  () => form.value.resolution,
  () => form.value.wikidataItem
], () => {
  if (applyMetadataToAll.value && isEditingBatchSource.value && !isHydratingBatchForm) {
    updateSourceMetadataFromEditor()
  }
}, { deep: true, flush: "sync" })

/* ------------------------------------------------------------------ */
/*  Resume state (Feature #2)                                         */
/* ------------------------------------------------------------------ */

const resumeBanner = ref<{ refresh: () => void } | null>(null)

// In-session resume: when an upload error happens, the uploader keeps the
// last filekey/offset. If the user still has the bytes in memory
// (lastEncodedBytes), we can offer a Resume button on the error screen.
const lastEncodedBytes = ref<UploadPayload | null>(null)
const lastEncodeMeta = ref<{
  encodeMs: number; audioIncluded: boolean; passthrough: boolean
  outputKey?: string
} | null>(null)
const resumingExisting = ref(false)

const canResumeInSession = computed(() => {
  return state.value === "error" &&
    lastEncodedBytes.value !== null &&
    uploader.resumeState.value !== null &&
    uploader.resumeState.value.offset > 0
})

const inSessionResumePercent = computed(() => {
  const rs = uploader.resumeState.value
  if (!rs) return 0
  return Math.round((rs.offset / rs.totalBytes) * 100)
})

/* ------------------------------------------------------------------ */
/*  Existing computed values (kept verbatim)                          */
/* ------------------------------------------------------------------ */

const encoderPhase = computed(() => encoder.phase.value)
const encodeProgressPct = computed(() => Math.round(encoder.progress.value * 100))
const uploadProgressPct = computed(() => Math.round(uploader.progress.value * 100))
const uploadedBytes = computed(() => uploader.uploadedBytes.value)
const encodedBytes = computed(() =>
  encoder.lastResult.value?.totalBytes ??
  (lastEncodedBytes.value ? payloadSize(lastEncodedBytes.value) : 0))

const encodeStart = ref(0)
const uploadStart = ref(0)

const encodeETA = computed(() => {
  if (!encodeStart.value || encoder.progress.value <= 0.05) return ""
  const elapsed = (performance.now() - encodeStart.value) / 1000
  const total = elapsed / encoder.progress.value
  return formatDuration(Math.max(0, total - elapsed))
})
const uploadSpeed = computed(() => {
  if (!uploadStart.value || uploadedBytes.value === 0) return ""
  const elapsedSec = (performance.now() - uploadStart.value) / 1000
  if (elapsedSec < 0.5) return ""
  return formatBytes(uploadedBytes.value / elapsedSec) + "/s"
})
const uploadETA = computed(() => {
  if (!uploadStart.value || uploader.progress.value <= 0.05) return ""
  const elapsed = (performance.now() - uploadStart.value) / 1000
  const total = elapsed / uploader.progress.value
  return formatDuration(Math.max(0, total - elapsed))
})

// `willPassthrough` originally checked codec/container alone. With trim,
// we also need to defeat passthrough when trim is active - even VP9/WebM
// must be re-encoded so timestamps align.
const willPassthrough = computed(() => {
  if (!file.value || !meta.value) return false
  const isWebM = meta.value.containerFormat === "webm" || meta.value.containerFormat === "matroska"
  const isVP = meta.value.videoCodec.startsWith("vp9") ||
               meta.value.videoCodec.startsWith("vp8") ||
               meta.value.videoCodec === "vp09" || meta.value.videoCodec === "vp08"
  if (!(isWebM && isVP)) return false
  if (form.value.mediaMode === "video-only") return false
  // Audio-only always re-encodes: the video track is dropped and the
  // container changes from WebM to Ogg.
  if (form.value.mediaMode === "audio-only") return false
  // Trim defeats passthrough.
  const trim = form.value.trim
  if (trim) {
    const startTrim = trim.start != null && trim.start > 0.05
    const endTrim = trim.end != null && (meta.value.duration - trim.end) > 0.05
    if (startTrim || endTrim) return false
  }
  return true
})

/**
 * The real title budget, which is what validateTitle enforces: the cap
 * minus the dot and the extension. Showing MAX_TITLE_LENGTH here would tell
 * a contributor "238/240" and then refuse the upload - and the number now
 * differs between .webm and .ogg output.
 */
const usableTitleLength = computed(() =>
  MAX_TITLE_LENGTH - outputExtension.value.length - 1)

/**
 * What the validator actually measures.
 *
 * The counter used to read the raw field while validateTitle() measured the
 * sanitized string, so a title whose last character was a space counted as
 * 236 and validated as 235: the field said "236/235" and then accepted it.
 * One number, taken from one place.
 */
const sanitizedTitleLength = computed(() => sanitizeTitle(form.value.title).length)

const isAudioOnly = computed(() => form.value.mediaMode === "audio-only")
const outputExtension = computed(() => outputExtensionFor(form.value.mediaMode))

const titleError = computed(() => {
  if (!form.value.title) return ""
  const v = validateTitle(form.value.title, outputExtension.value)
  if (v.ok) return ""
  return v.reasonKey ? t(v.reasonKey, v.reasonParams || {}) : "Invalid title"
})

const titleAvailability = ref<"" | "checking" | "available" | "taken">("")
let titleCheckTimer: number | undefined

watch(() => form.value.title, (newVal) => {
  clearTimeout(titleCheckTimer)
  if (!newVal || titleError.value) { titleAvailability.value = ""; return }
  titleAvailability.value = "checking"
  titleCheckTimer = window.setTimeout(async () => {
    try {
      const clean = sanitizeTitle(newVal)
      const filename = clean + "." + outputExtension.value
      const available = await checkFilenameAvailable(filename)
      if (sanitizeTitle(form.value.title) !== clean) return
      titleAvailability.value = available ? "available" : "taken"
    } catch { titleAvailability.value = "" }
  }, 500)
})

let authorOverrideTimer: number | undefined
watch(
  () => form.value.authorOverride,
  (value) => {
    clearTimeout(authorOverrideTimer)
    authorOverrideTimer = window.setTimeout(() => {
      try {
        const trimmed = value.trim()

        if (trimmed) {
          localStorage.setItem(AUTHOR_OVERRIDE_KEY, trimmed)
        } else {
          localStorage.removeItem(AUTHOR_OVERRIDE_KEY)
        }
      } catch {
        // Ignore storage failures
      }
    }, 500)
  }
)

// Effective duration after trim, used for the over-20-min check.
const effectiveDuration = computed(() => {
  if (!meta.value) return 0
  const trim = form.value.trim
  if (!trim) return meta.value.duration
  const start = trim.start != null && trim.start > 0 ? trim.start : 0
  const end = trim.end != null && trim.end < meta.value.duration ? trim.end : meta.value.duration
  return Math.max(0, end - start)
})

/* ------------------------------------------------------------------ */
/*  Submit gate - walks the new provenance tree                       */
/* ------------------------------------------------------------------ */

interface Blocker { key: string; params?: Record<string, unknown> }

/**
 * Walks the provenance tree and returns EVERY blocking reason it finds.
 * Returning all of them (vs. just the first) lets the UI render a
 * checklist so the user sees the complete fix-list instead of fixing
 * one issue and being immediately told about the next.
 *
 * Mirrors UploadWizard's invariants:
 *   - own + contains-others: othersStatus must be free-license or
 *     not-protected; copyright-protected and unknown are hard blocks.
 *   - own license must be preset, or non-empty custom.
 *   - third reason must be set and not "unknown".
 *   - third sourceUrl must be a valid http/https URL.
 *   - third author must be filled OR unknownAuthor checked.
 *   - third creator-released/heir/permission require a valid license
 *     ("I don't know" allowed only for creator-released).
 *   - third not-protected requires at least one PD checkbox OR a
 *     non-empty custom PD tag.
 */
function checkProvenanceAll(): Blocker[] {
  const out: Blocker[] = []
  const p = form.value.provenance
  if (p.kind === "own") {
    if (p.ownership.kind === "contains-others") {
      const s = p.ownership.othersStatus
      if (s == null) out.push({ key: "submit_disabled.own_others_unset" })
      else if (s === "copyright-protected") out.push({ key: "submit_disabled.own_others_protected" })
      else if (s === "unknown") out.push({ key: "submit_disabled.own_others_unknown" })
    }
    if (p.license.kind === "preset") {
      // valid
    } else if (p.license.kind === "custom") {
      if (!p.license.template.trim()) out.push({ key: "submit_disabled.own_license_custom_empty" })
    } else {
      out.push({ key: "submit_disabled.own_license_missing" })
    }
    return out
  }
  // third-party
  if (p.reason == null) out.push({ key: "submit_disabled.third_reason_unset" })
  else if (p.reason === "unknown") out.push({ key: "submit_disabled.third_unknown_block" })
  // sourceUrl required regardless of reason
  const url = p.sourceUrl.trim()
  if (!url) out.push({ key: "submit_disabled.third_url_missing" })
  else {
    try {
      const u = new URL(url)
      if (u.protocol !== "http:" && u.protocol !== "https:") {
        out.push({ key: "submit_disabled.third_url_invalid" })
      }
    } catch { out.push({ key: "submit_disabled.third_url_invalid" }) }
  }
  // author OR unknownAuthor required
  if (!p.unknownAuthor && !p.originalAuthor.trim()) {
    out.push({ key: "submit_disabled.third_author_missing" })
  }
  // Branch-specific license
  if (p.reason === "creator-released" || p.reason === "heir" || p.reason === "permission") {
    if (!p.license) {
      out.push({ key: "submit_disabled.third_license_missing" })
    } else if (p.license.kind === "preset") {
      // valid
    } else if (p.license.kind === "custom") {
      if (!p.license.template.trim()) out.push({ key: "submit_disabled.third_license_custom_empty" })
    } else if (p.license.kind === "unknown") {
      // "I don't know" only valid for creator-released - heir &
      // permission must commit to a license.
      if (p.reason !== "creator-released") {
        out.push({ key: "submit_disabled.third_license_missing" })
      }
    }
  }
  if (p.reason === "not-protected") {
    const hasReason = (p.pdReasons || []).length > 0
    const hasCustomTag = (p.pdTag || "").trim().length > 0
    if (!hasReason && !hasCustomTag) out.push({ key: "submit_disabled.third_pd_unset" })
  }
  return out
}

/**
 * False while the date field is showing a complaint.
 *
 * The field already refused 12-50-2333, but refusing it only moved the
 * model: form.date kept its last good value and the upload went ahead with
 * that, silently, under a date the contributor never chose. A visible
 * error now stops the queue instead.
 */
const dateValid = ref(true)

const canSubmit = computed(() => {
  if (titleError.value || !form.value.title.trim() || !meta.value) return false
  if (!dateValid.value) return false
  if (effectiveDuration.value > MAX_DURATION_SECONDS) return false
  if (meta.value.width === 0 || meta.value.height === 0) return false
  return checkProvenanceAll().length === 0
})

/** Aggregate list of all blockers, rendered as a checklist above the
 *  submit button. Each item is the i18n-resolved string for one block.
 *  The list is empty when canSubmit is true. */
const submitDisabledReasons = computed(() => {
  const out: string[] = []
  if (!meta.value) return out
  if (!form.value.title.trim()) out.push(t('submit_disabled.no_title'))
  if (titleError.value) out.push(t('submit_disabled.title_error'))
  if (!dateValid.value) out.push(t('submit_disabled.date_invalid'))
  if (effectiveDuration.value > MAX_DURATION_SECONDS) {
    out.push(t('submit_disabled.over_duration', {
      limit: Math.round(MAX_DURATION_SECONDS / 60)
    }))
  }
  if (meta.value.width === 0 || meta.value.height === 0) {
    out.push(t('submit_disabled.no_dimensions'))
  }
  for (const b of checkProvenanceAll()) {
    out.push(t(b.key, b.params || {}))
  }
  return out
})

const submitButtonLabel = computed(() => {
  if (isBatchEditing.value) {
    // queue.start_button carries an {n}. Calling it without one rendered
    // the button as "Start queue ( files)".
    return t('queue.start_button', { n: batchItems.value.length })
  }
  return willPassthrough.value ? t('form.submit_passthrough') : t('form.submit_convert')
})

onMounted(async () => {
  // Always detect capabilities first, even on the callback route - the
  // banner will show on the post-callback main page too.
  detectCapabilities().then(c => { capabilities.value = c })

  if (route.value === "callback") return

  // Opt-in "keep me signed in": resume before deciding whether to show the
  // login screen, so a returning user never sees it.
  if (restoringSession.value) {
    try { await restoreSession() } catch { /* never fatal */ }
    authVersion.value++
    restoringSession.value = false
  }

  if (!isLoggedIn.value) return
  try {
    const u = await checkUser()
    userName.value = u.name
    setCachedUsername(u.name)
    // The author/source fields used to be set from the username here; they
    // are now derived from the SourceProvenance choice in buildWikitext at
    // upload time, so this assignment is no longer needed.
  } catch (err) {
    errorMessage.value = friendlyError(err)
    state.value = "error"
  }
})

/* ------------------------------------------------------------------ */
/*  Single-file flow (preserved)                                      */
/* ------------------------------------------------------------------ */

async function onFileSelected(f: File) {
  // Single-file path. Clears any batch state from a previous run.
  batchItems.value = []
  batchCurrentIndex.value = -1
  applyMetadataToAll.value = false

  file.value = f
  const draft = loadDraftForFile(f)
  if (draft) {
    Object.assign(form.value, draft)
  } else {
    clearDraft()
  }
  state.value = "analyzing"
  duplicateMatches.value = null
  try {
    const m = await probeFile(f)
    meta.value = m
    if (m.creationDate && form.value.date === todayStr) {
      form.value.date = m.creationDate
    }
    // Passthrough-only browsers (Firefox Android, older Safari): refuse
    // anything that would need re-encoding, with a clear message.
    if (capabilities.value && !capabilities.value.canEncode &&
        !isPassthroughEligible(m)) {
      errorMessage.value = t('capability.refuse_passthrough_only', {
        codec: m.videoCodec || "unknown",
        container: m.containerFormat || "unknown"
      })
      state.value = "error"
      return
    }
    const titleFromFile = defaultTitleFromFilename(f.name)
    if (titleFromFile && !form.value.title) form.value.title = titleFromFile
    state.value = "ready"

    // Fire-and-forget duplicate check. Runs in a worker so it doesn't block
    // the form. If it finds matches, we surface them via the duplicateMatches
    // ref + dialog. The user can ignore the warning and proceed; this is
    // advisory, not blocking.
    runDuplicateCheck(f)
  } catch (err) {
    errorMessage.value = friendlyError(err)
    state.value = "error"
  }
}

/* ------------------------------------------------------------------ */
/*  SHA-1 duplicate detection (Feature: pre-upload check)             */
/* ------------------------------------------------------------------ */

/** Most recent duplicate-check result. Null = no check run yet. Empty
 *  array = check ran, no matches. Non-empty = matches found. */
const duplicateMatches = ref<DuplicateMatch[] | null>(null)
const duplicateDialogOpen = ref(false)
const duplicateCheckProgress = ref<number | null>(null)

let activeSha1Worker: Worker | null = null

/** Files larger than this skip the duplicate-check entirely. SHA-1 of a
 *  500 MB file in a JS worker takes 15-30 seconds even on a fast machine;
 *  we'd rather not block the form for that long when the duplicate-check
 *  is just an advisory. The user can still upload a perceptual duplicate
 *  by sheer chance - that's true regardless of file size. */
const DUP_CHECK_MAX_BYTES = 100 * 1024 * 1024

function runDuplicateCheck(f: File) {
  duplicateMatches.value = null
  duplicateCheckProgress.value = null

  // Skip on memory-limited devices.
  if (capabilities.value?.details.memoryLimited) return
  // Skip on large files.
  if (f.size > DUP_CHECK_MAX_BYTES) return

  // Tear down any prior in-flight check (user picked a new file).
  if (activeSha1Worker) {
    activeSha1Worker.terminate()
    activeSha1Worker = null
  }
  duplicateCheckProgress.value = 0

  const worker = new Worker(
    new URL("./workers/sha1.worker.ts", import.meta.url),
    { type: "module" }
  )
  activeSha1Worker = worker

  worker.onmessage = async (e: MessageEvent<Sha1ResultMessage>) => {
    const data = e.data
    if (data.type === "progress") {
      duplicateCheckProgress.value = data.progress
    } else if (data.type === "done") {
      duplicateCheckProgress.value = null
      worker.terminate()
      if (activeSha1Worker === worker) activeSha1Worker = null
      try {
        const matches = await findBySha1(data.hash)
        duplicateMatches.value = matches
        if (matches.length > 0) duplicateDialogOpen.value = true
      } catch {
        duplicateMatches.value = []
      }
    } else if (data.type === "error") {
      duplicateCheckProgress.value = null
      worker.terminate()
      if (activeSha1Worker === worker) activeSha1Worker = null
    }
  }

  worker.onerror = () => {
    duplicateCheckProgress.value = null
    worker.terminate()
    if (activeSha1Worker === worker) activeSha1Worker = null
  }

  worker.postMessage({ type: "hash", file: f } as Sha1RequestMessage)
}

/** User-initiated skip of the in-flight duplicate check. */
function skipDuplicateCheck() {
  if (activeSha1Worker) {
    activeSha1Worker.terminate()
    activeSha1Worker = null
  }
  duplicateCheckProgress.value = null
}

const duplicateDialogMessage = computed(() => {
  const matches = duplicateMatches.value
  if (!matches || matches.length === 0) return ""
  const head = t('duplicate.dialog_intro', { n: matches.length })
  const lines = matches.slice(0, 5).map(m => "• " + m.title.replace(/^File:/, "")
    + (m.uploader ? " (" + m.uploader + ")" : "")
  ).join("\n")
  return head + "\n\n" + lines + "\n\n" + t('duplicate.dialog_outro')
})

function onDuplicateCancel() {
  duplicateDialogOpen.value = false
  resetAll()
}

/** Whether `meta` describes a file we can upload as-is without re-encoding.
 *  Mirrors the willPassthrough heuristic but works at file-pick time
 *  (before willPassthrough has a value). */
function isPassthroughEligible(m: VideoMeta): boolean {
  const isWebM = m.containerFormat === "webm" || m.containerFormat === "matroska"
  const isVP = m.videoCodec.startsWith("vp9") ||
               m.videoCodec.startsWith("vp8") ||
               m.videoCodec === "vp09" || m.videoCodec === "vp08"
  return isWebM && isVP
}

/**
 * TrimControl loaded its <video> element and got a different duration than
 * what probeFile (mediabunny) returned. Patch our local meta object so
 * FileMetadata's chips and over-limit warnings reflect the corrected value.
 *
 * Also patch the corresponding batch item's meta if we're inside a batch
 * editing session. Without that the batch queue would still show the wrong
 * duration for the current item once the user navigates away and back.
 */
function onTrimDurationUpdate(d: number) {
  if (!Number.isFinite(d) || d <= 0) return
  if (meta.value && Math.abs(d - meta.value.duration) > 0.5) {
    meta.value = { ...meta.value, duration: d }
  }
  const idx = batchCurrentIndex.value
  if (idx >= 0 && idx < batchItems.value.length) {
    const item = batchItems.value[idx]
    if (item.meta && Math.abs(d - item.meta.duration) > 0.5) {
      item.meta = { ...item.meta, duration: d }
    }
  }
}

/* ------------------------------------------------------------------ */
/*  Batch flow (Feature #1)                                           */
/* ------------------------------------------------------------------ */

function makeBatchId(): string {
  return "b_" + Date.now() + "_" + Math.random().toString(36).slice(2, 8)
}

async function onFilesSelected(files: File[]) {
  if (files.length === 0) return
  if (files.length === 1) { onFileSelected(files[0]); return }

  // Reset previous state, then build queue.
  resetForNewWork()

  const items: BatchItem[] = files.map((f) => {
    const titleFromFile = defaultTitleFromFilename(f.name)
    return {
      id: makeBatchId(),
      file: f,
      meta: null,
      form: {
        ...makeFreshForm(),
        title: titleFromFile || ""
      },
      status: "pending" as const,
      progress: 0
    }
  })



  batchItems.value = items
  batchCurrentIndex.value = 0
  await loadBatchItem(0)
}

/** A fresh queue entry for a file that has not been probed yet. */
function makePendingBatchItem(f: File): BatchItem {
  return {
    id: makeBatchId(),
    file: f,
    meta: null,
    form: {
      ...makeFreshForm(),
      title: defaultTitleFromFilename(f.name) || ""
    },
    status: "pending" as const,
    progress: 0
  }
}

/** Collapsed until asked for, and closed again once files land. */
const addMoreOpen = ref(false)
const canAddMore = computed(() =>
  state.value === "ready" && queueSlotsLeft.value > 0 && (!!file.value || batchItems.value.length > 0))

/** How many more files this queue can take. */
const queueSlotsLeft = computed(() => {
  const used = batchItems.value.length > 0
    ? batchItems.value.length
    : (file.value ? 1 : 0)
  return Math.max(0, MAX_QUEUE_FILES - used)
})

/**
 * Append files to the queue.
 *
 * When only a single file is loaded there is no queue yet, so the first
 * thing this does is promote it into one - carrying its probe result and
 * whatever has already been typed into the form, which is the whole point
 * of adding rather than starting over.
 */
async function onAddMoreFiles(files: File[]) {
  if (files.length === 0 || state.value !== "ready") return
  const accepted = files.slice(0, queueSlotsLeft.value)
  if (accepted.length === 0) return

  if (batchItems.value.length === 0) {
    if (!file.value || !meta.value) return
    batchItems.value = [{
      id: makeBatchId(),
      file: file.value,
      meta: meta.value,
      form: cloneUploadForm(form.value),
      status: "pending" as const,
      progress: 0
    }]
    batchCurrentIndex.value = 0
  } else {
    // Keep the open editor's edits before the list changes under it.
    const cur = batchItems.value[batchCurrentIndex.value]
    if (cur && cur.status === "pending") cur.form = cloneUploadForm(form.value)
  }

  batchItems.value.push(...accepted.map(makePendingBatchItem))
  // Collapse again: the queue above now shows what was added, and leaving
  // the dropzone open just pushes it out of view.
  addMoreOpen.value = false
}

async function loadBatchItem(idx: number) {
  if (idx < 0 || idx >= batchItems.value.length) return
  const item = batchItems.value[idx]
  batchCurrentIndex.value = idx

  item.status = "analyzing"
  state.value = "analyzing"
  file.value = item.file
  // Hydrate the editor form with this item's saved per-file values.
  hydrateFormFromBatchItem(item)

  try {
    const m = await probeFile(item.file)
    item.meta = m
    meta.value = m
    if (m.creationDate && item.form.date === todayStr) {
      isHydratingBatchForm = true
      item.form.date = m.creationDate
      form.value.date = m.creationDate
      isHydratingBatchForm = false
    }
    item.status = "pending"
    state.value = "ready"
  } catch (err) {
    item.errorMessage = err instanceof Error ? err.message : String(err)
    item.status = "error"
    advancePastFailedItem()
  }
}

function advancePastFailedItem() {
  // Find the next pending item; if none, surface as global error.
  const nextIdx = findNextPending(batchCurrentIndex.value + 1)
  if (nextIdx >= 0) {
    loadBatchItem(nextIdx)
  } else {
    state.value = "idle"
  }
}

function findNextPending(from: number): number {
  for (let i = from; i < batchItems.value.length; i++) {
    if (batchItems.value[i].status === "pending") return i
  }
  return -1
}

function onBatchRemove(id: string) {
  const idx = batchItems.value.findIndex(b => b.id === id)
  if (idx < 0) return
  // Don't remove the actively-processing item.
  if (idx === batchCurrentIndex.value &&
      (batchItems.value[idx].status === "encoding" || batchItems.value[idx].status === "uploading")) {
    return
  }
  batchItems.value.splice(idx, 1)
  if (idx < batchCurrentIndex.value) {
    batchCurrentIndex.value -= 1
  }
  if (batchItems.value.length === 0) {
    resetAll()
  }
}

async function onBatchSelect(id: string) {
  const idx = batchItems.value.findIndex(b => b.id === id)
  if (idx < 0 || idx === batchCurrentIndex.value) return

  // Only allow switching to pending items while in the editing state.
  const target = batchItems.value[idx]
  if (target.status !== 'pending' || state.value !== 'ready') return

  // Save current form edits back to the outgoing batch item.
  const prevIdx = batchCurrentIndex.value
  if (prevIdx >= 0 && prevIdx < batchItems.value.length) {
    batchItems.value[prevIdx].form = {
      ...form.value,
      captions: form.value.captions.map(d => ({ ...d })),
      descriptions: form.value.descriptions.map(d => ({ ...d })),
      provenance: cloneProvenance(form.value.provenance),
      categories: [...form.value.categories],
      wikidataItem: form.value.wikidataItem ? { ...form.value.wikidataItem } : null
    }
  }

  // If the target hasn't been probed yet (lazy probing), use loadBatchItem
  // which handles the analyzing → probe → ready lifecycle.
  if (!target.meta) {
    await loadBatchItem(idx)
    return
  }

  // Already probed - fast-switch without re-probing.
  batchCurrentIndex.value = idx
  file.value = target.file
  meta.value = target.meta
  hydrateFormFromBatchItem(target)
}

function hydrateFormFromBatchItem(item: BatchItem) {
  isHydratingBatchForm = true
  form.value = {
    ...item.form,
    captions: item.form.captions.map(d => ({ ...d })),
    descriptions: item.form.descriptions.map(d => ({ ...d })),
    provenance: cloneProvenance(item.form.provenance),
    categories: [...item.form.categories],
    wikidataItem: item.form.wikidataItem ? { ...item.form.wikidataItem } : null
  }
  isHydratingBatchForm = false
}

function withSharedMetadata(target: UploadForm, source: UploadForm): UploadForm {
  return {
    ...target,
    title: source.title,
    captions: source.captions.map(d => ({ ...d })),
    descriptions: source.descriptions.map(d => ({ ...d })),
    date: source.date,
    provenance: cloneProvenance(source.provenance),
    categories: [...source.categories],
    resolution: source.resolution,
    wikidataItem: source.wikidataItem ? { ...source.wikidataItem } : null
  }
}

/** Persist only shared fields from the index-0 editor into the stable source. */
function updateSourceMetadataFromEditor() {
  const source = sourceBatchItem.value
  if (!source) return
  source.form = withSharedMetadata(source.form, form.value)
  if (applyMetadataToAll.value) applySourceMetadataToPendingItems()
}

/** Apply index-0 source metadata onto other pending items. Trim and
 *  source-specific settings remain per-item. */
function applySourceMetadataToPendingItems() {
  const source = sourceBatchItem.value
  if (!source) return
  for (let i = 0; i < batchItems.value.length; i++) {
    if (i === SOURCE_BATCH_INDEX) continue
    const item = batchItems.value[i]
    if (item.status !== "pending") continue
    item.form = withSharedMetadata(item.form, source.form)
  }
}

function onLiveMetadataToggle() {
  if (!applyMetadataToAll.value || !isSourceAvailable.value) return
  if (isEditingBatchSource.value) {
    updateSourceMetadataFromEditor()
    return
  }
  applySourceMetadataToPendingItems()
}

function cloneProvenance(p: UploadForm["provenance"]): UploadForm["provenance"] {
  // Deep-clone so spread copies don't share nested object references
  // (ownership object, pdReasons array, license object).
  if (p.kind === "own") {
    return {
      kind: "own",
      ownership: { ...p.ownership },
      license: { ...p.license }
    }
  }
  return {
    kind: "third-party",
    reason: p.reason,
    sourceUrl: p.sourceUrl,
    originalAuthor: p.originalAuthor,
    unknownAuthor: p.unknownAuthor,
    isAiGenerated: p.isAiGenerated,
    license: p.license ? { ...p.license } : undefined,
    pdReasons: p.pdReasons ? [...p.pdReasons] : undefined,
    pdTag: p.pdTag
  }
}

/* ------------------------------------------------------------------ */
/*  Submit - single + batch                                           */
/* ------------------------------------------------------------------ */

async function onSubmit() {
  if (!file.value || !meta.value) return
  const v = validateTitle(form.value.title, outputExtension.value)
  if (!v.ok) {
    errorMessage.value = v.reasonKey ? t(v.reasonKey, v.reasonParams || {}) : "Invalid title"
    state.value = "error"; return
  }

  // Save edits back into the current batch item before kicking off.
  if (isBatchEditing.value && batchCurrentIndex.value >= 0) {
    const item = batchItems.value[batchCurrentIndex.value]
    item.form = cloneUploadForm(form.value)
    await runBatchQueue()
    return
  }

  await runUploadCycle()
}

function cloneUploadForm(source: UploadForm): UploadForm {
  return {
    ...source,
    captions: source.captions.map(d => ({ ...d })),
    descriptions: source.descriptions.map(d => ({ ...d })),
    provenance: cloneProvenance(source.provenance),
    categories: [...source.categories],
    wikidataItem: source.wikidataItem ? { ...source.wikidataItem } : null,
    trim: source.trim ? { ...source.trim } : null
  }
}

/** Process pending batch items in their original selection order. This cursor
 * is deliberately separate from batchCurrentIndex, which only controls the
 * editor selection. */
async function runBatchQueue() {
  if (batchProcessing.value) return
  batchProcessing.value = true
  let completedAll = false

  try {
    for (let index = 0; index < batchItems.value.length; index++) {
      const item = batchItems.value[index]
      if (item.status !== "pending") continue

      batchProcessingIndex.value = index
      let itemMeta = item.meta
      try {
        if (!itemMeta) {
          item.status = "analyzing"
          state.value = "analyzing"
          itemMeta = await probeFile(item.file)
          item.meta = itemMeta
        }
      } catch (err) {
        item.errorMessage = friendlyError(err)
        item.status = "error"
        errorMessage.value = item.errorMessage
        state.value = "error"
        return
      }

      const completed = await processBatchItem(item, itemMeta)
      if (!completed) return
    }

    const firstDone = batchItems.value.find(item => item.result)
    if (firstDone?.result) result.value = firstDone.result
    state.value = "done"
    completedAll = true
  } finally {
    batchProcessing.value = false
    batchProcessingIndex.value = -1
    if (completedAll) {
      lastEncodedBytes.value = null
      lastEncodeMeta.value = null
    }
  }
}

/** Encode and upload one captured batch item. No async callback reads the
 * mutable editor selection or form. */
async function processBatchItem(item: BatchItem, itemMeta: VideoMeta): Promise<boolean> {
  const itemForm = cloneUploadForm(item.form)
  const itemExtension = outputExtensionFor(itemForm.mediaMode)
  const titleValidation = validateTitle(itemForm.title, itemExtension)
  if (!titleValidation.ok) {
    item.errorMessage = titleValidation.reasonKey
      ? t(titleValidation.reasonKey, titleValidation.reasonParams || {})
      : "Invalid title"
    item.status = "error"
    errorMessage.value = item.errorMessage
    state.value = "error"
    return false
  }

  state.value = "encoding"
  item.status = "encoding"
  item.progress = 0
  encodeStart.value = performance.now()

  let encResult
  try {
    encResult = await encoder.encode(
      item.file, itemMeta, itemForm.resolution,
      itemForm.trim ?? null, itemForm.mediaMode ?? "video-audio"
    )
    lastEncodedBytes.value = encResult.bytes
    lastEncodeMeta.value = {
      encodeMs: encResult.encodeMs,
      audioIncluded: encResult.audioIncluded,
      passthrough: encResult.passthrough,
      outputKey: encResult.outputKey
    }
  } catch (err) {
    if (err instanceof Error && err.message === "cancelled") {
      item.status = "cancelled"
      state.value = "cancelled"
      return false
    }
    item.errorMessage = friendlyError(err)
    item.status = "error"
    errorMessage.value = item.errorMessage
    state.value = "error"
    return false
  }

  state.value = "uploading"
  item.status = "uploading"
  item.progress = 0
  uploadStart.value = performance.now()

  try {
    const uploadResult = await uploader.upload(
      encResult.bytes, itemForm, itemExtension,
      encResult.encodeMs, encResult.audioIncluded, encResult.passthrough,
      { sourceFile: item.file, outputKey: encResult.outputKey }
    )
    item.status = "done"
    item.result = uploadResult
    item.progress = 1
    lastEncodedBytes.value = null
    lastEncodeMeta.value = null
    return true
  } catch (err) {
    if (err instanceof Error && err.message === "cancelled") {
      item.status = "cancelled"
      state.value = "cancelled"
      return false
    }
    if (err instanceof UploadWarningsError) {
      // Preserve the existing warning dialog and stop before another item
      // can start. The current item remains recoverable via its upload stash.
      batchWarningContext.value = { item, form: itemForm }
      return false
    }
    if (uploader.resumeState.value && lastEncodedBytes.value && lastEncodeMeta.value) {
      batchResumeContext.value = { item, meta: itemMeta, form: itemForm }
    }
    item.errorMessage = friendlyError(err)
    item.status = "error"
    errorMessage.value = item.errorMessage
    state.value = "error"
    return false
  }
}

/** Encode + upload the currently-loaded file. After it completes, in batch
 *  mode this auto-advances to the next pending item. */
async function runUploadCycle(opts?: { resumeFrom?: ResumeState | null }) {
  if (!file.value || !meta.value) return

  const currentItem = isBatchEditing.value && batchCurrentIndex.value >= 0
    ? batchItems.value[batchCurrentIndex.value]
    : null

  // Encoding ----------------------------------------------------------
  resumingExisting.value = !!opts?.resumeFrom
  state.value = "encoding"
  if (currentItem) currentItem.status = "encoding"
  encodeStart.value = performance.now()

  let encResult
  if (opts?.resumeFrom && lastEncodedBytes.value && lastEncodeMeta.value) {
    // Resuming an in-session error: skip re-encoding, reuse the bytes we
    // already produced last time.
    encResult = {
      bytes: lastEncodedBytes.value,
      totalBytes: payloadSize(lastEncodedBytes.value),
      encodeMs: lastEncodeMeta.value.encodeMs,
      audioIncluded: lastEncodeMeta.value.audioIncluded,
      passthrough: lastEncodeMeta.value.passthrough,
      outputKey: lastEncodeMeta.value.outputKey
    }
  } else {
    try {
      encResult = await encoder.encode(
        file.value, meta.value,
        form.value.resolution,
        form.value.trim ?? null,
        form.value.mediaMode ?? "video-audio"
      )
      lastEncodedBytes.value = encResult.bytes
      lastEncodeMeta.value = {
        encodeMs: encResult.encodeMs,
        audioIncluded: encResult.audioIncluded,
        passthrough: encResult.passthrough,
        outputKey: encResult.outputKey
      }
    } catch (err) {
      if (err instanceof Error && err.message === "cancelled") {
        if (currentItem) currentItem.status = "cancelled"
        state.value = "cancelled"; return
      }
      const msg = friendlyError(err)
      if (currentItem) {
        currentItem.errorMessage = msg
        currentItem.status = "error"
      }
      errorMessage.value = msg
      state.value = "error"; return
    }
  }

  // Uploading ---------------------------------------------------------
  state.value = "uploading"
  if (currentItem) currentItem.status = "uploading"
  uploadStart.value = performance.now()

  try {
    const uploadResult = await uploader.upload(
      encResult.bytes, form.value, outputExtension.value,
      encResult.encodeMs, encResult.audioIncluded, encResult.passthrough,
      {
        resumeFrom: opts?.resumeFrom ?? null,
        // The source File is handed over for EVERY upload now, not only
        // passthroughs. Its head/tail/size hash identifies what the
        // contributor picked, which is what a resume has to match them
        // against. The encoded bytes being non-deterministic is no longer an
        // argument against it: the output is kept on disk, so a resume reuses
        // those bytes rather than trying to reproduce them.
        sourceFile: file.value,
        outputKey: encResult.outputKey
      }
    )

    if (currentItem) {
      currentItem.status = "done"
      currentItem.result = uploadResult
      currentItem.progress = 1
    }

    // In batch mode: keep the global state on "uploading" and immediately
    // start the next item, so the user sees a continuous flow with the
    // queue panel above.
    if (isBatchEditing.value) {
      const nextIdx = findNextPending(batchCurrentIndex.value + 1)
      // Clear bytes between items.
      lastEncodedBytes.value = null
      lastEncodeMeta.value = null
      if (nextIdx >= 0) {
        await loadBatchItem(nextIdx)
        // Auto-fire next: same form values were already filled per-item.
        await runUploadCycle()
        return
      }
      // All done - pick a representative result for the result card. We
      // surface the first successful item; the queue panel shows the rest.
      const firstDone = batchItems.value.find(b => b.result)
      if (firstDone?.result) {
        result.value = firstDone.result
        state.value = "done"
      } else {
        const anyError = batchItems.value.some(b => b.status === "error")
        state.value = anyError ? "error" : "cancelled"
        if (anyError) {
          errorMessage.value = batchItems.value
            .filter(b => b.errorMessage)
            .map(b => b.file.name + ": " + b.errorMessage)
            .join("\n")
        }
      }
      return
    }

    // Single-file: original behavior.
    result.value = uploadResult
    state.value = "done"
  } catch (err) {
    if (err instanceof Error && err.message === "cancelled") {
      if (currentItem) currentItem.status = "cancelled"
      state.value = "cancelled"; return
    }
    if (err instanceof UploadWarningsError) {
      // Not a hard failure - the user needs to review and confirm. The
      // dialog renders because uploader.pendingWarnings was just set.
      // Stay in 'uploading' state visually (it's effectively still in progress).
      return
    }
    const msg = friendlyError(err)
    if (currentItem) {
      currentItem.errorMessage = msg
      currentItem.status = "error"
    }
    errorMessage.value = msg
    state.value = "error"
  } finally {
    resumingExisting.value = false
  }
}

// Live progress -> queue item progress (for the visible bar in the queue panel)
watch(() => uploader.progress.value, (p) => {
  if (!batchProcessing.value || batchProcessingIndex.value < 0) return
  const item = batchItems.value[batchProcessingIndex.value]
  if (item && item.status === "uploading") item.progress = p
})
watch(() => encoder.progress.value, (p) => {
  if (!batchProcessing.value || batchProcessingIndex.value < 0) return
  const item = batchItems.value[batchProcessingIndex.value]
  if (item && item.status === "encoding") item.progress = p
})

function cancelEncode() {
  // Terminate the encoder worker first.
  encoder.cancel()
  // Then return all the way to the picker. Without this the user is left
  // staring at a frozen progress bar with no way back except a manual
  // page refresh - which several real users (Brave, DuckDuckGo) hit and
  // legitimately reported as "cancel just pauses". Releasing file +
  // meta references here also lets the browser GC the input file's
  // ArrayBuffer; otherwise it sits in memory until the next pick.
  resetForNewWork()
  state.value = "idle"
}
function cancelUpload() {
  uploader.cancel()
  resetForNewWork()
  state.value = "idle"
}

/* ------------------------------------------------------------------ */
/*  Resume handlers                                                   */
/* ------------------------------------------------------------------ */

/** Banner resume where the encoded output is still on disk: no file to pick,
 *  no encode to repeat, and byte-identical to what Commons already holds. */
async function onResumeStored(rs: ResumeState) {
  if (await tryResumeFromDisk(rs)) return
  // Swept between the banner rendering and the click. Fall back to asking.
  showInfo(t('resume.dialog_expired_title'), t('resume.alert_stash_expired'), "info")
  await clearResumeState(rs.id)
  if (resumeBanner.value) resumeBanner.value.refresh()
  state.value = "idle"
}

async function onResumeRequested(rs: ResumeState, picked: File) {
  // User clicked Resume in the banner; they re-picked the file. Probe to
  // hydrate metadata, then run the upload cycle directly with resumeFrom.
  resetForNewWork()
  file.value = picked
  state.value = "analyzing"
  try {
    const m = await probeFile(picked)
    meta.value = m
  } catch (err) {
    errorMessage.value = friendlyError(err)
    state.value = "error"
    return
  }

  // Cross-page resume used to work only for passthrough uploads: the saved
  // filekey points at bytes Commons already holds part of, and a re-encode
  // cannot be trusted to reproduce them, so splicing a fresh encode onto a
  // half-finished stash could publish a corrupt file.
  //
  // That argument is gone when the output is still on disk, because then
  // there is nothing to reproduce - resumeFromDisk above carries on with the
  // very same bytes. This branch is what is left: the output has been swept
  // or the browser has no OPFS, so the encode has to happen again, and a
  // fresh upload is the only safe way to take it.
  if (!rs.passthrough) {
    showInfo(t('resume.dialog_reencode_title'), t('resume.alert_reencode_restart'), "info")
    form.value = {
      ...makeFreshForm(),
      title: defaultTitleFromFilename(picked.name) || ""
    }
    state.value = "ready"
    return
  }

  // Identity hash check: when the resume entry has one, the file the user
  // re-picked must have the same head/tail/size hash. Catches "same size,
  // different bytes" mismatches that the size-only check on ResumeBanner
  // can't see.
  if (rs.identityHash) {
    try {
      const pickedHash = await computeIdentityHash(picked)
      if (pickedHash !== rs.identityHash) {
        showInfo(
          t('resume.dialog_mismatch_title'),
          t('resume.alert_mismatch_hash'),
          "warn"
        )
        await clearResumeState(rs.id)
        if (resumeBanner.value) resumeBanner.value.refresh()
        state.value = "idle"
        return
      }
    } catch {
      // crypto.subtle unavailable; let the upload proceed and fall back to
      // size-based identity from the original ResumeBanner check.
    }
  }

  // Stash pre-flight: ask Commons whether the saved filekey is still valid
  // before kicking off chunks. Stashes expire silently after ~6 hours and
  // a stale filekey causes a confusing mid-chunk verification error.
  const stillStashed = await stashFileExists(rs.filekey)
  if (!stillStashed) {
    showInfo(
      t('resume.dialog_expired_title'),
      t('resume.alert_stash_expired'),
      "info"
    )
    await clearResumeState(rs.id)
    if (resumeBanner.value) resumeBanner.value.refresh()
    // Fall through to the form so the user can start a fresh upload of
    // the same file without re-picking.
    form.value = {
      ...makeFreshForm(),
      title: defaultTitleFromFilename(picked.name) || ""
    }
    state.value = "ready"
    return
  }

  // Passthrough resume path: the bytes ARE the file. Run upload directly.
  // Captions and the Wikidata item come back from the entry, because the
  // stored wikitext cannot carry them (see restoredPostUploadFields).
  const resumedForm = makeFreshForm()
  form.value = {
    ...resumedForm,
    title: rs.filename.replace(/\.(webm|ogg)$/i, ""),
    ...restoredPostUploadFields(rs, resumedForm.captions)
  }
  const bytes = await picked.arrayBuffer()
  lastEncodedBytes.value = bytes
  lastEncodeMeta.value = { encodeMs: 0, audioIncluded: !!meta.value?.audioCodec, passthrough: true }
  await runUploadCycle({ resumeFrom: rs })
}

/**
 * Resume straight from the encoded output still on disk.
 *
 * This is the case the contributor actually hits: the encode finished, the
 * upload got most of the way, and then the phone locked or the network
 * moved. Before, that meant re-picking the source and encoding the whole
 * thing again - and it did not even get that far, because the banner
 * compared the chosen file against the ENCODED size and declared the right
 * file to be the wrong one.
 *
 * Returns false when there is nothing on disk to resume from, and the caller
 * falls back to asking for the source file.
 */
async function tryResumeFromDisk(rs: ResumeState): Promise<boolean> {
  const stored = await openEncodedFile(rs.outputKey)
  if (!stored) return false
  // The stash expires after about six hours; without this the first chunk
  // fails with a confusing verification error instead of a clear message.
  if (!(await stashFileExists(rs.filekey))) return false

  resetForNewWork()
  const resumedForm = makeFreshForm()
  form.value = {
    ...resumedForm,
    title: rs.filename.replace(/\.(webm|ogg)$/i, ""),
    ...restoredPostUploadFields(rs, resumedForm.captions)
  }
  lastEncodedBytes.value = stored
  lastEncodeMeta.value = {
    encodeMs: rs.encodeMs,
    audioIncluded: rs.audioIncluded,
    passthrough: rs.passthrough,
    outputKey: rs.outputKey
  }
  await runUploadCycle({ resumeFrom: rs })
  return true
}

async function onResumeInSession() {
  // Pulled from uploader.resumeState which was left intact on error.
  const rs = uploader.resumeState.value
  if (!rs || !lastEncodedBytes.value) return
  const batchContext = batchResumeContext.value
  if (batchContext) {
    if (batchProcessing.value) return
    batchProcessing.value = true
    batchProcessingIndex.value = batchItems.value.indexOf(batchContext.item)
    state.value = "uploading"
    batchContext.item.status = "uploading"
    batchContext.item.progress = 0
    try {
      const enc = lastEncodeMeta.value
      const uploadResult = await uploader.upload(
        lastEncodedBytes.value, batchContext.form,
        outputExtensionFor(batchContext.form.mediaMode),
        enc?.encodeMs ?? 0, enc?.audioIncluded ?? false, enc?.passthrough ?? false,
        {
          resumeFrom: rs,
          sourceFile: batchContext.item.file,
          outputKey: enc?.outputKey
        }
      )
      batchContext.item.status = "done"
      batchContext.item.result = uploadResult
      batchContext.item.progress = 1
      batchResumeContext.value = null
      lastEncodedBytes.value = null
      lastEncodeMeta.value = null
    } catch (err) {
      if (err instanceof Error && err.message === "cancelled") {
        batchContext.item.status = "cancelled"
        state.value = "cancelled"
      } else if (err instanceof UploadWarningsError) {
        batchWarningContext.value = { item: batchContext.item, form: batchContext.form }
        batchResumeContext.value = null
      } else {
        batchContext.item.errorMessage = friendlyError(err)
        batchContext.item.status = "error"
        errorMessage.value = batchContext.item.errorMessage
        state.value = "error"
      }
      return
    } finally {
      batchProcessing.value = false
      batchProcessingIndex.value = -1
    }
    await runBatchQueue()
    return
  }
  await runUploadCycle({ resumeFrom: rs })
}

/* ------------------------------------------------------------------ */
/*  Server warning confirmation                                       */
/* ------------------------------------------------------------------ */

/** User said "no, I'll fix it" - drop the stash and clear state. */
async function onWarningsCancel() {
  if (batchWarningContext.value) {
    const { item } = batchWarningContext.value
    item.status = "error"
    item.errorMessage = t('queue.warning_confirmation_cancelled')
    batchWarningContext.value = null
    uploader.pendingWarnings.value = null
    uploader.reset()
    errorMessage.value = item.errorMessage
    state.value = "error"
    return
  }
  uploader.pendingWarnings.value = null
  uploader.reset()
  // Stay on whatever state we were in before; the form is still there if the
  // user wants to rename and re-upload.
  state.value = "ready"
  errorMessage.value = ""
}

/** User explicitly accepted the warnings. Retry finalize with ignorewarnings;
 *  bytes are already stashed under the same filekey, so this is fast. */
async function onWarningsConfirm() {
  const pw = uploader.pendingWarnings.value
  if (!pw || !lastEncodedBytes.value) return
  // Synthesize a resumeFrom that points at the stashed filekey at the byte
  // boundary equal to totalBytes - chunkedUpload will skip the chunk loop
  // and go straight to finalize, this time with ignorewarnings=1.
  const rs = uploader.resumeState.value
  if (!rs) {
    uploader.pendingWarnings.value = null
    return
  }

  const batchContext = batchWarningContext.value
  if (batchContext) {
    uploader.pendingWarnings.value = null
    state.value = "uploading"
    try {
      const enc = lastEncodeMeta.value
      const uploadResult = await uploader.upload(
        lastEncodedBytes.value, batchContext.form,
        outputExtensionFor(batchContext.form.mediaMode),
        enc?.encodeMs ?? 0, enc?.audioIncluded ?? false, enc?.passthrough ?? false,
        {
          resumeFrom: rs,
          confirmWarnings: true,
          sourceFile: batchContext.item.file,
          outputKey: enc?.outputKey
        }
      )
      batchContext.item.status = "done"
      batchContext.item.result = uploadResult
      batchContext.item.progress = 1
      batchWarningContext.value = null
      lastEncodedBytes.value = null
      lastEncodeMeta.value = null
      await runBatchQueue()
    } catch (err) {
      if (err instanceof Error && err.message === "cancelled") {
        batchContext.item.status = "cancelled"
        state.value = "cancelled"
      } else {
        batchContext.item.errorMessage = friendlyError(err)
        batchContext.item.status = "error"
        errorMessage.value = batchContext.item.errorMessage
        state.value = "error"
      }
      batchWarningContext.value = null
    }
    return
  }

  uploader.pendingWarnings.value = null
  state.value = "uploading"
  try {
    const enc = lastEncodeMeta.value
    const uploadResult = await uploader.upload(
      lastEncodedBytes.value, form.value, outputExtension.value,
      enc?.encodeMs ?? 0, enc?.audioIncluded ?? false, enc?.passthrough ?? false,
      { resumeFrom: rs, confirmWarnings: true }
    )
    result.value = uploadResult
    state.value = "done"
  } catch (err) {
    if (err instanceof Error && err.message === "cancelled") {
      state.value = "cancelled"; return
    }
    errorMessage.value = friendlyError(err)
    state.value = "error"
  }
}

/* ------------------------------------------------------------------ */
/*  Reset                                                             */
/* ------------------------------------------------------------------ */

function resetForNewWork() {
  // Clear file/meta/result/error but preserve userName, the date, and the
  // author override. The author override is a "remember last value" field
  // (issue #16): carrying it across uploads in the same session mirrors the
  // localStorage restore that happens on a fresh page load.
  file.value = null; meta.value = null; result.value = null
  errorMessage.value = ""; encodeStart.value = 0; uploadStart.value = 0
  encoder.reset(); uploader.reset()
  lastEncodedBytes.value = null
  lastEncodeMeta.value = null
  resumingExisting.value = false
  titleAvailability.value = ""
  // The field re-announces itself on mount, but the editor is not always
  // torn down between uploads, so clear the flag here as well.
  dateValid.value = true
  form.value = {
    ...makeFreshForm(),
    date: form.value.date,
    authorOverride: form.value.authorOverride
  }
}
function resetAuthorOverride() {
  form.value.authorOverride = ""
  // The watcher on authorOverride handles localStorage cleanup;
  // no need to call localStorage.removeItem here.
}

function resetAll() {
  resetForNewWork()
  batchItems.value = []
  batchCurrentIndex.value = -1
  applyMetadataToAll.value = false
  batchWarningContext.value = null
  batchResumeContext.value = null
  state.value = "idle"
  // Refresh the resume banner in case a state was just cleared.
  resumeBanner.value?.refresh()
}

async function copyError() {
  try {
    await navigator.clipboard.writeText(errorMessage.value)
    copyOk.value = true
    setTimeout(() => copyOk.value = false, 2000)
  } catch { /* ignore */ }
}

async function copyTitle() {
  if (!form.value.title) return
  try {
    await navigator.clipboard.writeText(form.value.title)
    titleCopyOk.value = true
    setTimeout(() => titleCopyOk.value = false, 2000)
  } catch { /* ignore */ }
}

async function copyDescription() {
  const text = primaryDescription.value
  if (!text) return
  try {
    await navigator.clipboard.writeText(text)
    descriptionCopyOk.value = true
    setTimeout(() => descriptionCopyOk.value = false, 2000)
  } catch { /* ignore */ }
}

function onLogout() {
  if (isUserOAuthMode()) oauthLogout()
  else showInfo(t('app.dialog_legacy_title'), t('app.dialog_legacy_msg'), "warn")
}

function formatBytes(bytes: number): string {
  if (!bytes || bytes < 0) return "0 B"
  if (bytes < 1024) return bytes + " B"
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB"
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(2) + " MB"
  return (bytes / (1024 * 1024 * 1024)).toFixed(2) + " GB"
}

function formatDuration(sec: number): string {
  if (!sec || !isFinite(sec) || sec < 0) return ""
  if (sec < 60) return Math.ceil(sec) + "s"
  const m = Math.floor(sec / 60); const s = Math.round(sec % 60)
  return m + "m" + (s > 0 ? " " + s + "s" : "")
}

watch(state, (s) => {
  if (s === "done" || s === "cancelled" || s === "error") {
    window.scrollTo({ top: 0, behavior: "smooth" })
  }
  if (s === "done") {
    // Upload succeeded - the draft form no longer needs to survive a
    // reload. See T434086.
    clearDraft()
  }
})

/* ------------------------------------------------------------------ */
/*  beforeunload guard                                                */
/* ------------------------------------------------------------------ */

/**
 * When the user is mid-encode or mid-upload, browsers should warn before
 * navigation/tab-close. Returning a string (or calling preventDefault on
 * the event) triggers the native "Leave site?" prompt - modern browsers
 * always show a generic message regardless of the string returned.
 *
 * Per MDN: only registered while a destructive close would actually lose
 * work. We don't want to nag during idle / form / done / error.
 */
function beforeUnloadHandler(e: BeforeUnloadEvent): string | undefined {
  e.preventDefault()
  // Some browsers still read returnValue / the return string; a
  // non-empty string is required for them to honor the prompt.
  e.returnValue = ""
  return ""
}

function isWorkInFlight(s: AppState): boolean {
  return s === "encoding" || s === "uploading" || s === "analyzing"
}

watch(state, (s, oldState) => {
  const wasInFlight = oldState ? isWorkInFlight(oldState) : false
  const nowInFlight = isWorkInFlight(s)
  if (nowInFlight && !wasInFlight) {
    window.addEventListener("beforeunload", beforeUnloadHandler)
    // A phone that locks its screen suspends the tab, which suspends the
    // worker: the encode stops partway and the contributor comes back to a
    // frozen job. Phones lock after 30 seconds and these take minutes.
    void requestWakeLock()
  } else if (!nowInFlight && wasInFlight) {
    window.removeEventListener("beforeunload", beforeUnloadHandler)
    void releaseWakeLock()
  }
})

</script>

<style>
/* Cards were butting into one another with nothing between them. */
.c2c-main { display: flex; flex-direction: column; gap: 14px; }

.c2c-app {
  max-width: 680px; margin: 0 auto; padding: 20px 16px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif;
  color: #202122;
}
.c2c-pre-header { display: flex; justify-content: flex-end; padding: 8px 0 0 0; }
.c2c-header {
  display: flex; justify-content: space-between; align-items: center;
  flex-wrap: wrap; gap: 10px;
  padding-bottom: 14px; border-bottom: 1px solid #eaecf0;
  margin-bottom: 18px;
}
/* Brief placeholder while a remembered session is resumed. */
.c2c-restoring {
  min-height: 40vh;
  display: flex; align-items: center; justify-content: center;
}
.c2c-restoring::after {
  content: "";
  width: 28px; height: 28px; border-radius: 50%;
  border: 3px solid var(--border-color-subtle, #c8ccd1);
  border-top-color: var(--background-color-progressive, #36c);
  animation: c2c-restoring-spin 0.8s linear infinite;
}
@keyframes c2c-restoring-spin { to { transform: rotate(360deg); } }

.c2c-brand-row { display: flex; align-items: center; gap: 8px; }
.c2c-logo { display: inline-flex; }
.c2c-brand { font-size: 18px; font-weight: 700; }
.c2c-header-right { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.c2c-user { display: flex; align-items: center; gap: 8px; font-size: 13px; }
/* Sign-out is the one destructive control in the header, so it looks like
   a control rather than a sentence, and the icon carries the meaning for
   anyone scanning rather than reading. */
.c2c-logout {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 5px 10px; min-height: 30px;
  font: inherit; font-size: 13px; font-weight: 500;
  color: #202122; background: #fff;
  border: 1px solid var(--border-color-base, #a2a9b1);
  border-radius: 6px; cursor: pointer;
  transition: background-color 120ms ease-out, border-color 120ms ease-out, color 120ms ease-out;
}
.c2c-logout:hover { background: #fee7e6; border-color: #d73333; color: #b32424; }
.c2c-logout:focus-visible {
  outline: 2px solid transparent;
  border-color: #3366cc;
  box-shadow: 0 0 0 3px rgba(51, 102, 204, 0.25);
}
.c2c-logout :deep(svg) { flex: 0 0 auto; }


.c2c-form { display: flex; flex-direction: column; gap: 12px; margin-top: 12px; }
.c2c-form-section { display: flex; flex-direction: column; gap: 10px; }
/* auto-fit, so a row holding a single field fills the width instead of
   sitting at half with dead space beside it. */
.c2c-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 12px; }
.c2c-field { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.c2c-field-header {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; margin-bottom: 4px; min-height: 28px;
}
.c2c-field label { font-weight: 600; font-size: 13px; color: #202122; }
/* Hints that live inside a label (the date format, the author help line)
   were inline and knocked the label text off its baseline. */
.c2c-field label small { display: block; font-weight: 400; margin-top: 2px; }
/* A hint that sits on the same line as its label, such as the date format.
   The rule below it is for hints that take their own line. */
.c2c-label-hint { font-weight: 400; font-size: 12px; color: var(--color-subtle, #54595d); }

.c2c-recommended {
  font-weight: 400;
  font-size: 12px;
  color: #14866d;
  margin-inline-start: 6px;
}
.c2c-field-note {
  margin-bottom: 4px;
  color: var(--color-subtle, #54595d);
  font-size: 12px;
  line-height: 1.45;
}

.c2c-optional {
  font-weight: 400;
  font-size: 12px;
  color: var(--color-subtle, #54595d);
  margin-inline-start: 6px;
}

/* Checkbox that reveals an optional section. */
.c2c-toggle {
  display: flex; align-items: center; gap: 8px;
  font-size: 13px; color: #202122; cursor: pointer;
  padding: 2px 0;
}
.c2c-toggle input { width: 16px; height: 16px; min-height: 0; cursor: pointer; }

/* The add-more row sits under the queue it feeds. Collapsed it is a single
   line; open it reveals the compact dropzone. */
.c2c-addmore { display: flex; flex-direction: column; gap: 8px; }
.c2c-addmore-toggle {
  display: inline-flex; align-items: center; gap: 8px;
  align-self: flex-start;
  padding: 7px 12px; min-height: 34px;
  font: inherit; font-size: 13px; font-weight: 600;
  color: #202122; background: #fff;
  border: 1px solid var(--border-color-base, #a2a9b1);
  border-radius: 6px; cursor: pointer;
  transition: background-color 120ms ease-out, border-color 120ms ease-out;
}
.c2c-addmore-toggle:hover { background: #eaecf0; }
.c2c-addmore-toggle:focus-visible {
  outline: 2px solid transparent;
  border-color: #3366cc;
  box-shadow: 0 0 0 3px rgba(51, 102, 204, 0.25);
}
.c2c-addmore-toggle small {
  font-weight: 400;
  color: var(--color-subtle, #54595d);
}
.c2c-addmore-full {
  margin: 0;
  font-size: 12px;
  color: var(--color-subtle, #54595d);
}

.c2c-inline-btn {
  align-self: flex-start;
  margin-top: 6px;
  background: #fff; color: #202122;
  border: 1px solid var(--border-color-base, #a2a9b1);
  border-radius: 6px;
  padding: 6px 12px; min-height: 32px;
  font: inherit; font-size: 13px; cursor: pointer;
}
.c2c-inline-btn:hover { background: var(--background-color-neutral, #eaecf0); }
.c2c-inline-btn:focus-visible {
  outline: 2px solid var(--background-color-progressive, #36c); outline-offset: 1px;
}
.c2c-field-grouplabel { font-weight: 600; font-size: 13px; color: #202122; }
.c2c-field small {
  color: #54595d; font-size: 12px; line-height: 1.4;
  /* A pasted 250-character word has no break opportunity, so without
     this it pushes the card wider than the page and the whole form
     gets a horizontal scrollbar. */
  overflow-wrap: anywhere;
  min-width: 0;
}
.c2c-field-error { color: #b32424 !important; font-weight: 600; }
.c2c-field-warn { color: #886600 !important; }
.c2c-field-info { color: #2a4b8d !important; }
.c2c-field-ok { color: #14866d !important; }
.c2c-field input,
.c2c-field textarea,
.c2c-field select {
  padding: 10px 12px; border: 1px solid #a2a9b1; border-radius: 6px;
  font: inherit;
  /* 16px font-size on inputs prevents iOS Safari from zooming in on focus.
     Below 16px, iOS treats the field as 'too small to type into' and
     auto-zooms, which jumps the viewport and is jarring on mobile. */
  font-size: 16px;
  box-sizing: border-box; width: 100%;
  /* 44px is the WCAG-recommended minimum touch target. */
  min-height: 44px;
}
.c2c-field textarea { resize: vertical; min-height: 88px; padding-top: 10px; }
.c2c-field input[aria-invalid="true"] { border-color: #b32424; }
.c2c-field input:focus,
.c2c-field textarea:focus,
.c2c-field select:focus {
  outline: 2px solid transparent;
  border-color: #3366cc;
  box-shadow: 0 0 0 3px rgba(51, 102, 204, 0.25);
}
.c2c-required { color: #d73333; }
.c2c-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 6px; }

.c2c-progress-section { text-align: center; padding: 28px 16px; }
.c2c-progress-section h2 { margin: 0 0 14px 0; font-size: 18px; color: #54595d; }
.c2c-bar { height: 10px; background: #eaecf0; border-radius: 5px; overflow: hidden; margin: 14px 0 8px 0; }
.c2c-bar-fill { height: 100%; background: #3366cc; transition: width 0.2s ease-out; }
.c2c-bar-fill--upload { background: #14866d; }
.c2c-progress-meta {
  display: flex; justify-content: center; flex-wrap: wrap;
  gap: 12px; font-size: 13px; color: #54595d; margin-bottom: 10px;
}
.c2c-hint { color: #54595d; font-size: 13px; margin: 0 0 14px 0; }

.c2c-info-section { text-align: center; padding: 28px 16px; }
.c2c-info-section h2 { color: #54595d; margin: 0 0 10px 0; }

.c2c-error-section { padding: 18px; background: #fee7e6; border: 1px solid #d73333; border-radius: 6px; }
.c2c-error-section h2 { margin: 0 0 10px 0; color: #b32424; font-size: 16px; }
.c2c-error-msg {
  background: white; border: 1px solid #d8d8d8; border-radius: 4px;
  padding: 10px; margin: 0 0 12px 0;
  white-space: pre-wrap; word-break: break-word;
  font: 12px/1.5 ui-monospace, monospace;
  max-height: 200px; overflow-y: auto;
}
.c2c-resume-inline {
  font-size: 13px; color: #886600; padding: 8px 10px;
  background: #fef6e7; border: 1px solid #f0d795;
  border-radius: 4px; margin: 0 0 10px 0;
}
.c2c-apply-all {
  display: flex; align-items: flex-start; gap: 6px;
  font-size: 13px; color: #54595d; padding: 4px 0; flex-wrap: wrap;
}

@media (max-width: 540px) {
  .c2c-app { padding: 10px 12px; }
  .c2c-header { flex-direction: column; align-items: flex-start; gap: 8px; }
  .c2c-header-right { width: 100%; justify-content: space-between; }
  .c2c-row { grid-template-columns: 1fr; }
  .c2c-actions { flex-direction: column-reverse; }
  .c2c-actions > * { width: 100%; }
  .c2c-main { gap: 10px; }
}

/*
 * Small phones, which is most of the audience.
 *
 * Below 380px the header wraps three ways, the queue rows run out of room
 * for their figures, and 44px controls side by side no longer fit. Nothing
 * here shrinks a touch target below 44px; the space comes out of padding
 * and gaps instead.
 */
@media (max-width: 380px) {
  .c2c-app { padding: 8px 10px; }
  .c2c-main { gap: 8px; }
  .c2c-header-right { gap: 6px; }
  .c2c-brand { font-size: 17px; }
  .c2c-field label { font-size: 12.5px; }
  .c2c-field small { font-size: 11.5px; }
  .c2c-logout span { display: none; }
  .c2c-logout { padding: 5px 8px; }
}

.c2c-feedback-strip {
  text-align: center;
  margin: 32px 0 16px 0;
  padding-top: 12px;
  border-top: 1px solid #eaecf0;
  font-size: 12px;
  color: #54595d;
}
.c2c-feedback-strip a {
  color: #3366cc;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.c2c-feedback-strip a:hover { text-decoration: underline; }
.c2c-feedback-strip a :deep(svg) { width: 12px; height: 12px; }

/* "What happens when you click Convert & Upload" panel - sets expectations
   for the encode/upload phase so users don't think a long encode means a
   broken tool. */
.c2c-preamble {
  background: #f8f9fa;
  border: 1px solid #c8ccd1;
  border-left: 3px solid #3366cc;
  border-radius: 4px;
  padding: 12px 14px;
  margin: 12px 0;
}
.c2c-preamble h3 {
  margin: 0 0 6px 0;
  font-size: 13px;
  font-weight: 600;
  color: #54595d;
}
.c2c-preamble ul {
  margin: 0;
  padding: 0 0 0 18px;
  font-size: 12px;
  color: #54595d;
  line-height: 1.55;
}
.c2c-preamble li { margin-bottom: 3px; }
.c2c-preamble li:last-child { margin-bottom: 0; }

/* Inline disabled-button reason. Yellow alert with a checklist of every
   blocking condition so the user sees the full fix-list at once. */
.c2c-submit-reason {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: #fef6e7;
  border: 1px solid #f0d795;
  border-radius: 4px;
  padding: 12px 14px;
  margin: 12px 0 4px 0;
  font-size: 13px;
  color: #886600;
  line-height: 1.55;
}
.c2c-submit-reason :deep(svg) { flex-shrink: 0; margin-top: 2px; }
.c2c-submit-reason-body { flex: 1; min-width: 0; }
.c2c-submit-reason-body strong { display: block; margin-bottom: 6px; font-size: 13px; }
.c2c-submit-reason-body ul { margin: 0; padding-left: 18px; }
.c2c-submit-reason-body li { margin-bottom: 2px; }
.c2c-submit-reason-body li:last-child { margin-bottom: 0; }

/* Background SHA-1 hash progress - small text, low-key. */
.c2c-dupe-progress {
  font-size: 11px;
  color: #72777d;
  margin: 4px 0 0 0;
  font-style: italic;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.c2c-dupe-skip {
  background: none;
  border: none;
  padding: 2px 6px;
  font: inherit;
  font-style: normal;
  font-size: 11px;
  color: #3366cc;
  cursor: pointer;
  border-radius: 3px;
}
.c2c-dupe-skip:hover { background: #eaf3ff; text-decoration: underline; }

/*
 * Laptop and desktop layout.
 *
 * Deliberately the last thing in this stylesheet. A media query adds no
 * specificity, so these rules have to come after the base rules they
 * override. The first version of this block sat higher up, above
 * `.c2c-form-section { display: flex }`, and silently never applied.
 */
@media (min-width: 1024px) {
  /* 680px reads well but leaves most of a laptop screen empty. */
  .c2c-app { max-width: 880px; }

}
</style>
